import "./TimeSuggestions.scss";

import classNames from "classnames";
import { getHoursOrDefault, getMinutesOrDefault } from "helpers";
import { useTranslations } from "providers";
import React, { useMemo } from "react";
import { createPortal } from "react-dom";

interface TimeSuggestionsProps {
  hourOptions: string[];
  minuteOptions: string[];
  anchorRect: DOMRect | null;
  visible: boolean;
  selectHours: (value: string) => void;
  selectMinutes: (value: string) => void;
  value: string;
  selectedHour: string | null;
  selectedMinute: string | null;
}

const offsetLeft = 8;
const minWidth = 450;

export const TimeSuggestions: React.FC<TimeSuggestionsProps> = ({
  hourOptions,
  minuteOptions,
  anchorRect,
  visible,
  selectHours,
  selectedHour,
  selectMinutes,
  selectedMinute,
  value,
}) => {
  const i18n = useTranslations();

  const style = useMemo<React.CSSProperties>(() => {
    if (!anchorRect) return { position: "fixed", top: 0, left: 0 };

    const viewportW = typeof window === "undefined" ? 1024 : window.innerWidth;
    const viewportH = typeof window === "undefined" ? 768 : window.innerHeight;

    const hoursRows = Math.ceil(hourOptions.length / 6);
    const minutesRows = Math.ceil(minuteOptions.length / 6);
    const panelEstimatedHeight = Math.min(Math.max(hoursRows, minutesRows) * 40 + 48, Math.max(160, viewportH - 32));

    // clamp left so panel stays inside viewport
    let left = anchorRect.left;
    if (left + minWidth + offsetLeft > viewportW) left = Math.max(offsetLeft, viewportW - minWidth - offsetLeft);
    if (left < offsetLeft) left = offsetLeft;

    // prefer placing below anchor; if not enough space, place above
    const belowTop = anchorRect.bottom + 4;
    let top = belowTop;
    if (belowTop + panelEstimatedHeight > viewportH - offsetLeft) {
      const aboveTop = anchorRect.top - 4 - panelEstimatedHeight;
      top = Math.max(offsetLeft, aboveTop);
    }

    return {
      top,
      left,
      minWidth,
      maxHeight: Math.max(120, viewportH - 32),
    };
  }, [anchorRect, hourOptions.length, minuteOptions.length]);

  const valueHours = getHoursOrDefault(value);
  const valueMinutes = getMinutesOrDefault(value);

  return createPortal(
    <div
      style={style}
      role="dialog"
      aria-label="Time suggestions panel"
      className={classNames("time-suggestions", {
        "time-suggestions--visible": visible,
        "time-suggestions--hidden": !visible,
      })}
    >
      <div className="time-suggestions-content">
        <div className="time-suggestions-left">
          <h5 className="time-suggestions-heading">{i18n.TimePicker_Hours}</h5>

          <div className="time-suggestions-hours-grid">
            {hourOptions.map((h) => (
              <button
                key={`h-${h}`}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                className={classNames({
                  "time-suggestions-item--active": valueHours === h,
                  "time-suggestions-item--selected": selectedHour === h,
                })}
                onClick={() => selectHours(h)}
              >
                {h}
              </button>
            ))}
          </div>
        </div>
        <div className="time-suggestions-divider" />
        <div className="time-suggestions-right">
          <h5 className="time-suggestions-heading">{i18n.TimePicker_Minutes}</h5>
          <div className="time-suggestions-minutes-grid">
            {minuteOptions.map((m) => (
              <button
                key={`m-${m}`}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                className={classNames({
                  "time-suggestions-item--active": valueMinutes === m,
                  "time-suggestions-item--selected": selectedMinute === m,
                })}
                onClick={() => selectMinutes(m)}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
